// Per-employee payroll profile (payroll-service): payroll inclusion, pay
// frequency/type, payroll group, and statutory applicability (PAYE/NSSF/WCF/
// SDL). Switching a statutory scheme off requires an exemption reason — the
// backend 400s with code `exemption_reason_required`, surfaced inline here.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Switch } from '@/features/shared/components/ui/switch';
import { Textarea } from '@/features/shared/components/ui/textarea';
import { BadgeDollarSign, Loader2, Save, Pencil, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { getPayrollProfile, setPayrollProfile, type PayrollProfile } from '@/services/payrollSettingsService';
import { getApiErrorMessage } from '@/lib/apiError';

const inputCls =
  'w-full h-10 px-3 rounded-md border border-border bg-card text-sm disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed';
const labelCls = 'text-xs text-muted-foreground';

const PAY_FREQUENCIES = ['monthly', 'weekly', 'biweekly', 'daily', 'ad_hoc'] as const;
const PAY_TYPES = ['salary', 'hourly', 'daily', 'piece_rate', 'commission', 'stipend'] as const;
const SDL_OPTIONS = ['auto', 'yes', 'no'] as const;

type Form = {
  payroll_enabled: boolean;
  pay_frequency: string;
  pay_type: string;
  payroll_group: string;
  paye_applicable: boolean;
  nssf_applicable: boolean;
  wcf_applicable: boolean;
  sdl_included: string;
  wcf_risk_category: string;
  statutory_exemption_reason: string;
};

const toForm = (p: PayrollProfile | null): Form => ({
  payroll_enabled: p?.payroll_enabled ?? true,
  pay_frequency: p?.pay_frequency || 'monthly',
  pay_type: p?.pay_type || 'salary',
  payroll_group: p?.payroll_group || '',
  paye_applicable: p?.paye_applicable ?? true,
  nssf_applicable: p?.nssf_applicable ?? true,
  wcf_applicable: p?.wcf_applicable ?? true,
  sdl_included: p?.sdl_included || 'auto',
  wcf_risk_category: p?.wcf_risk_category || '',
  statutory_exemption_reason: p?.statutory_exemption_reason || '',
});

export const PayrollProfileCard: React.FC<{ employeeId: string }> = ({ employeeId }) => {
  const { t } = useTranslation('employees');
  const [profile, setProfile] = useState<PayrollProfile | null>(null);
  const [form, setForm] = useState<Form>(toForm(null));
  const [loading, setLoading] = useState(true);
  const [notOnPayroll, setNotOnPayroll] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exemptionError, setExemptionError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getPayrollProfile(employeeId)
      .then((p) => { if (active) { setProfile(p); setForm(toForm(p)); setNotOnPayroll(false); } })
      .catch((e: any) => {
        if (!active) return;
        if (e?.response?.status === 404) setNotOnPayroll(true);
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [employeeId]);

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  const onSave = async () => {
    setSaving(true);
    setExemptionError(null);
    try {
      const updated = await setPayrollProfile(employeeId, {
        payroll_enabled: form.payroll_enabled,
        pay_frequency: form.pay_frequency,
        pay_type: form.pay_type,
        payroll_group: form.payroll_group.trim() || null,
        paye_applicable: form.paye_applicable,
        nssf_applicable: form.nssf_applicable,
        wcf_applicable: form.wcf_applicable,
        sdl_included: form.sdl_included,
        wcf_risk_category: form.wcf_risk_category.trim() || null,
        statutory_exemption_reason: form.statutory_exemption_reason.trim() || null,
      });
      setProfile(updated);
      setForm(toForm(updated));
      setEditing(false);
      toast.success(t('payroll_profile.saved', { defaultValue: 'Payroll profile updated.' }));
    } catch (e: any) {
      const data = e?.response?.data;
      const code = data?.code || data?.error?.code || data?.detail?.code;
      if (e?.response?.status === 400 && (code === 'exemption_reason_required' || JSON.stringify(data || {}).includes('exemption_reason_required'))) {
        setExemptionError(
          t('payroll_profile.exemption_required', {
            defaultValue: 'An exemption reason is required when switching a statutory scheme off.',
          }),
        );
      } else {
        toast.error(getApiErrorMessage(e, t('payroll_profile.save_failed', { defaultValue: 'Could not save payroll profile.' })));
      }
    } finally {
      setSaving(false);
    }
  };

  const freqLabel: Record<string, string> = {
    monthly: t('payroll_profile.freq_monthly', { defaultValue: 'Monthly' }),
    weekly: t('payroll_profile.freq_weekly', { defaultValue: 'Weekly' }),
    biweekly: t('payroll_profile.freq_biweekly', { defaultValue: 'Bi-weekly' }),
    daily: t('payroll_profile.freq_daily', { defaultValue: 'Daily' }),
    ad_hoc: t('payroll_profile.freq_ad_hoc', { defaultValue: 'Ad hoc' }),
  };
  const typeLabel: Record<string, string> = {
    salary: t('payroll_profile.type_salary', { defaultValue: 'Salary' }),
    hourly: t('payroll_profile.type_hourly', { defaultValue: 'Hourly' }),
    daily: t('payroll_profile.type_daily', { defaultValue: 'Daily rate' }),
    piece_rate: t('payroll_profile.type_piece_rate', { defaultValue: 'Piece rate' }),
    commission: t('payroll_profile.type_commission', { defaultValue: 'Commission' }),
    stipend: t('payroll_profile.type_stipend', { defaultValue: 'Stipend' }),
  };
  const sdlLabel: Record<string, string> = {
    auto: t('payroll_profile.sdl_auto', { defaultValue: 'Auto (headcount rule)' }),
    yes: t('payroll_profile.sdl_yes', { defaultValue: 'Yes' }),
    no: t('payroll_profile.sdl_no', { defaultValue: 'No' }),
  };

  return (
    <Card className="border-orange-100 dark:border-orange-500/20">
      <CardHeader>
        <CardTitle className="text-base text-orange-700 flex items-center gap-2">
          <BadgeDollarSign className="w-4 h-4" /> {t('payroll_profile.title', { defaultValue: 'Payroll profile' })}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="py-6 flex items-center justify-center text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('payroll_profile.loading', { defaultValue: 'Loading payroll profile…' })}
          </div>
        ) : notOnPayroll ? (
          <p className="text-sm text-muted-foreground">
            {t('payroll_profile.not_on_payroll', { defaultValue: 'This employee is not assigned to payroll yet. Assign them via the Compensation panel above first.' })}
          </p>
        ) : (
          <>
            {/* Payroll inclusion — the prominent switch */}
            <div className="flex items-center justify-between rounded-lg border border-border bg-muted/40 px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-foreground">
                  {t('payroll_profile.include', { defaultValue: 'Include in payroll' })}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('payroll_profile.include_hint', { defaultValue: 'When off, this person is skipped by payroll runs.' })}
                </p>
              </div>
              <Switch
                checked={form.payroll_enabled}
                onCheckedChange={(v) => set('payroll_enabled', v)}
                disabled={!editing}
                aria-label={t('payroll_profile.include', { defaultValue: 'Include in payroll' })}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <label className={labelCls}>{t('payroll_profile.pay_frequency', { defaultValue: 'Pay frequency' })}</label>
                <select className={inputCls} value={form.pay_frequency} onChange={(e) => set('pay_frequency', e.target.value)} disabled={!editing}
                  aria-label={t('payroll_profile.pay_frequency', { defaultValue: 'Pay frequency' })}>
                  {PAY_FREQUENCIES.map((f) => <option key={f} value={f}>{freqLabel[f]}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label className={labelCls}>{t('payroll_profile.pay_type', { defaultValue: 'Pay type' })}</label>
                <select className={inputCls} value={form.pay_type} onChange={(e) => set('pay_type', e.target.value)} disabled={!editing}
                  aria-label={t('payroll_profile.pay_type', { defaultValue: 'Pay type' })}>
                  {PAY_TYPES.map((f) => <option key={f} value={f}>{typeLabel[f]}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label className={labelCls}>{t('payroll_profile.group', { defaultValue: 'Payroll group' })}</label>
                <input className={inputCls} value={form.payroll_group} onChange={(e) => set('payroll_group', e.target.value)} disabled={!editing}
                  placeholder={t('payroll_profile.group_ph', { defaultValue: 'e.g. Head office' })} />
              </div>
            </div>

            {/* Statutory applicability */}
            <div className="border-t border-border pt-4 space-y-3">
              <p className="text-sm font-medium text-foreground">
                {t('payroll_profile.statutory', { defaultValue: 'Statutory applicability' })}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {([
                  ['paye_applicable', t('payroll_profile.paye', { defaultValue: 'PAYE' })],
                  ['nssf_applicable', t('payroll_profile.nssf', { defaultValue: 'NSSF' })],
                  ['wcf_applicable', t('payroll_profile.wcf', { defaultValue: 'WCF' })],
                ] as const).map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                    <span className="text-sm text-foreground">{label}</span>
                    <Switch checked={form[key]} onCheckedChange={(v) => set(key, v)} disabled={!editing} aria-label={label} />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className={labelCls}>{t('payroll_profile.sdl', { defaultValue: 'SDL included' })}</label>
                  <select className={inputCls} value={form.sdl_included} onChange={(e) => set('sdl_included', e.target.value)} disabled={!editing}
                    aria-label={t('payroll_profile.sdl', { defaultValue: 'SDL included' })}>
                    {SDL_OPTIONS.map((s) => <option key={s} value={s}>{sdlLabel[s]}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className={labelCls}>{t('payroll_profile.wcf_risk', { defaultValue: 'WCF risk category' })}</label>
                  <input className={inputCls} value={form.wcf_risk_category} onChange={(e) => set('wcf_risk_category', e.target.value)} disabled={!editing}
                    placeholder={t('payroll_profile.wcf_risk_ph', { defaultValue: 'e.g. low' })} />
                </div>
              </div>
              <div className="space-y-1">
                <label className={labelCls}>{t('payroll_profile.exemption', { defaultValue: 'Statutory exemption reason' })}</label>
                <Textarea
                  value={form.statutory_exemption_reason}
                  onChange={(e) => set('statutory_exemption_reason', e.target.value)}
                  disabled={!editing}
                  placeholder={t('payroll_profile.exemption_ph', { defaultValue: 'Required when a statutory scheme is switched off (e.g. exempt expatriate, covered elsewhere).' })}
                  className={exemptionError ? 'border-danger focus-visible:ring-danger' : ''}
                />
                {exemptionError && <p className="text-xs text-danger">{exemptionError}</p>}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {editing ? (
                <>
                  <Button onClick={onSave} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    {t('payroll_profile.save', { defaultValue: 'Save payroll profile' })}
                  </Button>
                  <Button variant="outline" disabled={saving}
                    onClick={() => { setForm(toForm(profile)); setExemptionError(null); setEditing(false); }}>
                    <X className="w-4 h-4 mr-1" /> {t('common.cancel', { defaultValue: 'Cancel' })}
                  </Button>
                </>
              ) : (
                <Button variant="outline" onClick={() => setEditing(true)}>
                  <Pencil className="w-4 h-4 mr-1" /> {t('common.edit', { defaultValue: 'Edit' })}
                </Button>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PayrollProfileCard;
